import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FiArrowLeft, FiCheckCircle, FiXCircle, FiWifi, FiCpu, FiActivity, FiEdit2, FiSettings } from "react-icons/fi";
import { MdSettingsInputAntenna, MdDeviceHub, MdRouter } from "react-icons/md";
import { IotmateButton, DialogBox, StatCard, InfoCard } from "../../components/Shared";
import { Table } from "../../components/Table";
import fetchDataFromApi from "../../api/IotmateRestApi";
import { generateDummyLoraGatewayDetails, dummyIrrigationLoraGateways, dummyDeviceList } from "../../Data/Data";
import "./LoraGateway.css";

const LoraGateway = () => {
  const { gatewayId } = useParams();
  const navigate = useNavigate();
  const [gatewayData, setGatewayData] = useState(null);
  const [attachedDevices, setAttachedDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [newName, setNewName] = useState("");

  useEffect(() => {
    let isInitialLoad = true;

    const fetchData = async () => {
      try {
        if (isInitialLoad) {
          setLoading(true);
        }
        const data = await fetchDataFromApi(`lora_gateway_details/${gatewayId}`);
        setGatewayData(data || generateDummyLoraGatewayDetails(gatewayId));
      } catch (error) {
        console.error("Error fetching LoRa gateway details:", error);
        setGatewayData(generateDummyLoraGatewayDetails(gatewayId));
      } finally {
        if (isInitialLoad) {
          setLoading(false);
          isInitialLoad = false;
        }
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, [gatewayId]);

  useEffect(() => {
    const fetchAttachedDevices = async () => {
      try {
        const data = await fetchDataFromApi(`lora_gateway_devices/${gatewayId}`);
        if (data && data.length > 0) {
          setAttachedDevices(data);
        } else {
          // Try to get control units from irrigation gateway data first
          const irrigationGateway = dummyIrrigationLoraGateways.find(gw => gw.gatewayId === gatewayId);
          if (irrigationGateway?.controlUnits) {
            setAttachedDevices(irrigationGateway.controlUnits);
          } else {
            // Fall back to device list child devices
            const deviceListGateway = dummyDeviceList.find(d => d.oms_id === gatewayId && d.type === "LoRa Gateway");
            setAttachedDevices(deviceListGateway?.childDevices || []);
          }
        }
      } catch (error) {
        console.error("Error fetching attached devices:", error);
        // Try to get control units from irrigation gateway data first
        const irrigationGateway = dummyIrrigationLoraGateways.find(gw => gw.gatewayId === gatewayId);
        if (irrigationGateway?.controlUnits) {
          setAttachedDevices(irrigationGateway.controlUnits);
        } else {
          // Fall back to device list child devices
          const deviceListGateway = dummyDeviceList.find(d => d.oms_id === gatewayId && d.type === "LoRa Gateway");
          setAttachedDevices(deviceListGateway?.childDevices || []);
        }
      }
    };

    fetchAttachedDevices();
  }, [gatewayId]);

  const handleEditClick = () => {
    setNewName(gatewayData?.name || "");
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setNewName("");
  };

  const handleSaveName = () => {
    if (newName.trim()) {
      setGatewayData({ ...gatewayData, name: newName.trim() });
      setIsDialogOpen(false);
      setNewName("");
    }
  };

  const handleSettingsClick = () => {
    navigate(`/gwsettings/${gatewayId}`);
  };

  const handleDeviceRowClick = React.useCallback((row) => {
    const device = row.original;
    const deviceId = device.deviceId || device.oms_id;
    const deviceType = device.type;

    // Navigate based on device type
    switch (deviceType) {
      case "Water Level":
        navigate(`/water-level/${deviceId}`);
        break;
      case "AQM":
        navigate(`/air-quality/${deviceId}`);
        break;
      case "Treated Water":
        navigate(`/treated-water/${deviceId}`);
        break;
      case "Irrigation":
        navigate(`/irrigation/${deviceId}`);
        break;
      default:
        // For irrigation control units (they have zones property)
        if (device.zones !== undefined) {
          navigate(`/irrigation/${deviceId}`);
        } else {
          navigate(`/control-unit/${deviceId}`);
        }
        break;
    }
  }, [navigate]);

  // Check if data is from irrigation (has zones) or device list
  const isIrrigationData = attachedDevices.length > 0 && attachedDevices[0].zones !== undefined;

  const deviceColumns = React.useMemo(
    () => isIrrigationData ? [
      {
        Header: "Device ID",
        accessor: "deviceId",
        Cell: ({ value }) => (
          <span style={{
            color: '#3b82f6',
            textDecoration: 'underline',
            fontWeight: '600'
          }}>
            {value}
          </span>
        ),
      },
      {
        Header: "Device Name",
        accessor: "name",
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: ({ value }) => (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: 'center' }}>
            {value === "Online" ? (
              <FiCheckCircle size={18} color="#10b981" />
            ) : (
              <FiXCircle size={18} color="#ef4444" />
            )}
            <span
              className={`status-badge ${
                value === "Online" ? "status-online" : "status-offline"
              }`}
            >
              {value}
            </span>
          </div>
        ),
      },
      {
        Header: "Zones",
        accessor: "zones",
        Cell: ({ value }) => (
          <span style={{ fontWeight: '600', color: '#374151' }}>
            {value}
          </span>
        ),
      },
      {
        Header: "Active Zones",
        accessor: "activeZones",
        Cell: ({ value }) => (
          <span style={{
            color: value > 0 ? '#10b981' : '#9ca3af',
            fontWeight: '600'
          }}>
            {value}
          </span>
        ),
      },
      {
        Header: "LoRa RX (dBm)",
        accessor: "loraRx",
        Cell: ({ value }) => (
          <span style={{
            color: value > -80 ? '#10b981' : '#ef4444',
            fontWeight: '600'
          }}>
            {value}
          </span>
        ),
      },
      {
        Header: "Last Update",
        accessor: "lastUpdate",
      },
    ] : [
      {
        Header: "Device ID",
        accessor: "oms_id",
        Cell: ({ value }) => (
          <span style={{
            color: '#3b82f6',
            textDecoration: 'underline',
            fontWeight: '600'
          }}>
            {value}
          </span>
        ),
      },
      {
        Header: "Device Name",
        accessor: "name",
      },
      {
        Header: "Type",
        accessor: "type",
        Cell: ({ value }) => (
          <span style={{ fontWeight: '500', color: '#374151' }}>
            {value}
          </span>
        ),
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: ({ value }) => (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', justifyContent: 'center' }}>
            {value === "Online" ? (
              <FiCheckCircle size={18} color="#10b981" />
            ) : (
              <FiXCircle size={18} color="#ef4444" />
            )}
            <span
              className={`status-badge ${
                value === "Online" ? "status-online" : "status-offline"
              }`}
            >
              {value}
            </span>
          </div>
        ),
      },
      {
        Header: "Connectivity",
        accessor: "connectivity",
        Cell: ({ value }) => (
          <span style={{ fontWeight: '500', color: '#667eea' }}>
            {value}
          </span>
        ),
      },
      {
        Header: "Last Update",
        accessor: "lastUpdate",
      },
    ],
    [isIrrigationData]
  );

  if (loading) {
    return (
      <div className="lora-gw-loading">
        <div className="spinner"></div>
        <p>Loading gateway details...</p>
      </div>
    );
  }

  if (!gatewayData) {
    return (
      <div className="lora-gw-error">
        <p>Failed to load gateway data</p>
        <button onClick={() => navigate(-1)} className="lora-gw-back-btn">
          Back
        </button>
      </div>
    );
  }

  return (
    <div className="lora-gw-container">
      {/* Header */}
      <div className="lora-gw-header">
        <button onClick={() => navigate(-1)} className="lora-gw-back-btn">
          <FiArrowLeft size={20} />
          Back
        </button>
        <div className="lora-gw-title-section">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <h1 className="lora-gw-title">
                {gatewayData.name || `LoRa Gateway ${gatewayId}`}
              </h1>
              <IotmateButton onClick={handleEditClick} variant="default" className="iotmate-btn-icon">
                <FiEdit2 size={18} />
              </IotmateButton>
            </div>
            <IotmateButton onClick={handleSettingsClick} variant="default" className="iotmate-btn-icon">
              <FiSettings size={18} />
            </IotmateButton>
          </div>
          <div className="lora-gw-status">
            {gatewayData.status === "Online" ? (
              <FiCheckCircle size={20} color="#10b981" />
            ) : (
              <FiXCircle size={20} color="#ef4444" />
            )}
            <span
              style={{
                color: gatewayData.status === "Online" ? "#10b981" : "#ef4444",
                fontWeight: "600"
              }}
            >
              {gatewayData.status || "Offline"}
            </span>
          </div>
        </div>
        <p className="lora-gw-last-update">
          Last updated: {gatewayData.lastUpdate || "N/A"}
        </p>
      </div>

      {/* Main Info Cards */}
      <div className="info-card-grid">
        <InfoCard
          icon={<MdRouter />}
          iconColor="#667eea"
          title="Gateway ID"
          value={gatewayData.gatewayId}
        />
        <InfoCard
          icon={<FiCpu />}
          iconColor="#8b5cf6"
          title="Version"
          value={gatewayData.version}
        />
        <InfoCard
          icon={<FiWifi />}
          iconColor="#10b981"
          title="WiFi Signal"
          value={`${gatewayData.wifiSignal} dBm`}
          subtitle={gatewayData.wifiSignal > -70 ? "Excellent" : "Good"}
        />
        <InfoCard
          icon={<MdDeviceHub />}
          iconColor="#f59e0b"
          title="Connected Devices"
          value={attachedDevices.length}
          subtitle={`${attachedDevices.filter(d => d.status === "Online").length} Online`}
        />
      </div>

      {/* Additional Info */}
      <div className="lora-gw-additional-info">
        <div className="lora-gw-info-item">
          <FiActivity size={20} />
          <span className="lora-gw-info-label">Battery:</span>
          <span className="lora-gw-info-value">{gatewayData.battery}%</span>
        </div>
        <div className="lora-gw-info-item">
          <MdSettingsInputAntenna size={20} />
          <span className="lora-gw-info-label">LoRa Frequency:</span>
          <span className="lora-gw-info-value">{gatewayData.loraFrequency} MHz</span>
        </div>
        <div className="lora-gw-info-item">
          <FiCpu size={20} />
          <span className="lora-gw-info-label">Uptime:</span>
          <span className="lora-gw-info-value">{gatewayData.uptime}</span>
        </div>
      </div>

      {/* Device Statistics */}
      <div className="lora-gw-device-stats">
        <StatCard
          value={attachedDevices.length}
          label={isIrrigationData ? "Total Control Units" : "Total Devices"}
          variant="total"
        />
        <StatCard
          value={attachedDevices.filter((d) => d.status === "Online").length}
          label="Online"
          variant="online"
        />
        <StatCard
          value={attachedDevices.filter((d) => d.status === "Offline").length}
          label="Offline"
          variant="offline"
        />
      </div>

      {/* Attached Devices Table */}
      <div className="lora-gw-devices-table">
        <Table
          columns={deviceColumns}
          data={attachedDevices}
          heading={isIrrigationData ? "Control Units" : "Attached Devices"}
          onClick={handleDeviceRowClick}
          typeComplex={true}
        />
      </div>

      {/* Edit Name Dialog */}
      <DialogBox
        isOpen={isDialogOpen}
        onClose={handleCloseDialog}
        title="Edit Gateway Name"
        onSave={handleSaveName}
        saveLabel="Save"
        cancelLabel="Cancel"
      >
        <input
          type="text"
          className="dialog-input"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          placeholder="Enter gateway name"
          autoFocus
        />
      </DialogBox>
    </div>
  );
};

export default LoraGateway;
